// src/rms/pages/SignAgreement.jsx
// ============================================================================
// /sign/:token — the customer reads and signs
// ============================================================================
// The first genuinely customer-facing screen in this product. Everything else
// behind /rms, /advertiser, /ad-sales and /admin needs an account; this needs a
// link, and the person holding it has probably never heard of ReserveZoo.
//
// ----------------------------------------------------------------------------
// The token comes out of the URL immediately
// ----------------------------------------------------------------------------
// A token in the path is sent as the Referer header on every outbound
// subresource the page requests. JoinInvite already strips one for exactly this
// reason. Here there is a second reason the ad units cannot explain away: the
// link is a credential that signs a contract, and the address bar is the first
// place it gets read aloud, screenshotted or pasted into a chat.
//
// So: read it, replaceState it away, keep it in sessionStorage so a reload
// still works. And <meta name="referrer" content="no-referrer"> for the window
// between mount and the effect.
//
// ----------------------------------------------------------------------------
// No ads, by construction
// ----------------------------------------------------------------------------
// This route is outside RmsLayout entirely, so the rail and the masthead are
// not suppressed here -- they were never mounted. That is deliberate and worth
// stating, because "no ads on the contract screen" is a promise that should not
// depend on anybody remembering a slot flag.
// ============================================================================

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import agreementDocumentService from "../data/agreementDocumentService";
import AgreementReader from "../components/AgreementReader";
import { Banner, SignaturePad } from "../components/ui";
import Brandmark from "../components/Brandmark";
import i18n from "../i18n";
import "../styles/rms.css";

/** Renders outside RmsProvider, so it translates through i18next directly. */
const t = (key, fallback, params) =>
  i18n.t(key, { defaultValue: fallback, ...(params || {}) });

const STASH = "rms.sign.token";

/** Read once, strip immediately, survive a reload. */
export function useStrippedToken(paramToken, stashKey) {
  const token = useRef(null);
  if (token.current === null) {
    if (paramToken) {
      try {
        window.sessionStorage.setItem(stashKey, paramToken);
      } catch {
        /* private mode: the token lives for this page load only */
      }
      const base = window.location.pathname.replace(/\/[^/]+$/, "");
      window.history.replaceState({}, "", base || "/");
      token.current = paramToken;
    } else {
      try {
        token.current = window.sessionStorage.getItem(stashKey) || "";
      } catch {
        token.current = "";
      }
    }
  }
  return token.current;
}

export default function SignAgreement() {
  const { token: paramToken } = useParams();
  const token = useStrippedToken(paramToken, STASH);

  const [doc, setDoc] = useState(null);
  const [problem, setProblem] = useState(null);
  const [loading, setLoading] = useState(true);

  const [name, setName] = useState("");
  const [consented, setConsented] = useState(false);
  const [signature, setSignature] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    // Belt and braces with the meta tag below: some browsers honour only one.
    document.title = t("sign.pageTitle", "Sign your agreement");
  }, []);

  const load = useCallback(async () => {
    const result = await agreementDocumentService.fetchForSigning(token);
    setLoading(false);
    if (!result.success) {
      setProblem(result.error);
      return;
    }
    setDoc(result.data);
    setName(result.data.signer_name || "");
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const sign = async () => {
    setBusy(true);
    setProblem(null);
    const result = await agreementDocumentService.signRemotely(token, {
      signerName: name,
      consented,
      signatureDataUrl: signature,
    });
    setBusy(false);
    if (!result.success) {
      setProblem(result.error);
      return;
    }
    try {
      window.sessionStorage.removeItem(STASH);
    } catch {
      /* nothing to clear */
    }
    setDone(result.data);
  };

  const canSign = !!doc && !!name.trim() && consented && !busy;

  // The frame every state shares. .rms-sign, not .rms-auth: that shell caps its
  // column at 420px, which is right for a password field and hopeless for a
  // two-thousand-word contract.
  const frame = (children) => (
    <div className="rms-root rms-sign">
      <meta name="referrer" content="no-referrer" />
      <div className="rms-sign-inner">
        <div className="rms-sign-brand">
          <Brandmark size="1.3em" />
        </div>
        <div className="rms-sign-card">{children}</div>
      </div>
    </div>
  );

  if (loading) {
    return frame(
      <p style={{ margin: 0, color: "var(--rms-muted-strong)", fontSize: 14.5 }}>
        {t("common.loading", "Loading…")}
      </p>
    );
  }

  if (problem && !doc) {
    return frame(<Banner tone="danger">{problem}</Banner>);
  }

  if (done) {
    return frame(
      <div className="rms-sign-done">
        <div className="rms-sign-tick" aria-hidden="true">&#10003;</div>
        <h1 className="rms-sign-title" style={{ marginBottom: 8 }}>
          {t("sign.thanksTitle", "That’s signed")}
        </h1>
        <p style={{ margin: "0 auto 20px", maxWidth: 420, fontSize: 14, lineHeight: 1.6, color: "var(--rms-muted-strong)" }}>
          {t(
            "sign.copyHelp",
            "A copy has been kept for you, with the date, the time and the exact wording you agreed to. This link does not expire — keep it."
          )}
        </p>
        <Link className="rms-btn rms-btn--primary" to={`/signed/${done.view_token}`}>
          {t("sign.openCopy", "Open my signed copy")}
        </Link>
      </div>
    );
  }

  return frame(
    <>
      <h1 className="rms-sign-title">{doc.title}</h1>
      <p className="rms-sign-from">
        {t("sign.from", "Sent to you by {business}", { business: doc.business_name })}
      </p>

      <p className="rms-sign-lede">
        {t(
          "sign.readFirst",
          "Please read this in full. Scroll to the end — the whole document is below, and signing means you agree to all of it."
        )}
      </p>

      <AgreementReader body={doc.body} maxHeight={380} />

      {problem ? <Banner tone="danger">{problem}</Banner> : null}

      <div style={{ marginTop: 20 }}>
        <label className="rms-sign-label" htmlFor="signer-name">
          {t("sign.yourName", "Your full name")}
        </label>
        <input
          id="signer-name"
          className="rms-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoComplete="name"
        />
      </div>

      <div style={{ marginTop: 16 }}>
        <span className="rms-sign-label">{t("sign.drawHere", "Sign here")}</span>
        <div className="rms-sign-pad">
          <SignaturePad onChange={setSignature} />
        </div>
      </div>

      {/* Its own tick, and not folded into the button. ESIGN and UETA treat
          agreeing to transact electronically as a separate agreement from the
          signature, and it is stored in its own column for the same reason. */}
      <label className="rms-checkline" style={{ marginTop: 16 }}>
        <input
          type="checkbox"
          checked={consented}
          onChange={(e) => setConsented(e.target.checked)}
        />
        {t(
          "sign.consent",
          "I have read this agreement, I intend to be bound by it, and I agree to sign it electronically and to receive it and related notices electronically."
        )}
      </label>

      <div className="rms-sign-actions">
        <button
          type="button"
          className="rms-btn rms-btn--primary"
          onClick={sign}
          disabled={!canSign}
        >
          {busy ? t("common.working", "Working…") : t("sign.signIt", "Sign this agreement")}
        </button>
      </div>

      <p className="rms-sign-fine">
        {t(
          "sign.paperCopy",
          "You can ask {business} for a paper copy at any time, and you can withdraw your agreement to electronic records for anything they send you in future.",
          { business: doc.business_name }
        )}
      </p>
    </>
  );
}
